import superagent from 'superagent'

/**
 * Device pairing approval (WEB-7089) against the API-3607 endpoints.
 *
 * All three calls are authenticated - the whole point of the flow is that the pairing is bound
 * to the approving user's account, so there is no anonymous path here. The car's own two
 * endpoints (Device/Code and Device/Token) are not called from the web app at all.
 *
 * The API rejects a wrong or unknown code with a 4xx and a message in the body, and it throttles
 * repeated failures per account, so every rejection here is shown to the user rather than
 * retried.
 */

// The API returns its failure reason as the response body. Prefer that over superagent's own
// "Forbidden"/"Not Found" text, which tells the user nothing about what to do next.
const messageFrom = (err, fallback) => {
  const body = err && err.response ? err.response.text : null

  if (!body) return fallback

  // Messages come back as a quoted JSON string; unwrap it rather than showing the quotes.
  const trimmed = body.trim()
  try {
    const parsed = JSON.parse(trimmed)
    if (typeof parsed === 'string' && parsed.length) return parsed
  } catch (e) {
    // Not JSON - fall through and use the raw body.
  }

  return trimmed.length ? trimmed : fallback
}

const withHeaders = request => request
  .set('Content-Type', 'application/json')
  .set('Platform', 'Web')
  .set('Version', `${__version__}`)

const deviceLink = {
  /**
   * What is being approved - device name, platform and when it was requested - so the user can
   * see what they are saying yes to instead of trusting an opaque code.
   */
  getPending: ({ userCode, token }) => {
    return new Promise((resolve, reject) => {
      withHeaders(superagent.get(`${__API_URI__}/Device/Pending`))
        .query({ userCode })
        .set('Authorization', `Bearer ${token}`)
        .end((err, res) => {
          if (err) {
            return reject(new Error(messageFrom(err, 'That code could not be found.')))
          }
          resolve(res.body)
        })
    })
  },

  approve: ({ userCode, token }) => {
    return new Promise((resolve, reject) => {
      withHeaders(superagent.post(`${__API_URI__}/Device/Approve`))
        .set('Authorization', `Bearer ${token}`)
        .send({ userCode })
        .end((err, res) => {
          if (err) {
            return reject(new Error(messageFrom(err, 'That pairing could not be approved.')))
          }
          resolve(res.body)
        })
    })
  },

  deny: ({ userCode, token }) => {
    return new Promise((resolve, reject) => {
      withHeaders(superagent.post(`${__API_URI__}/Device/Deny`))
        .set('Authorization', `Bearer ${token}`)
        .send({ userCode })
        .end((err, res) => {
          if (err) {
            return reject(new Error(messageFrom(err, 'That pairing could not be declined.')))
          }
          resolve(res.body)
        })
    })
  }
}

export default deviceLink
